import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadApiFile, saveBlob } from '../../api/download';
import {
  deleteMapping,
  exportMappings,
  listMappings,
  mappingsWorkbookExportPath,
  upsertMapping,
} from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { mappingKinds, type MappingKind, type TenantMapping } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { useNoTenantMessageKey, useSelectedTenant } from '../../tenant/tenantSelection';
import { ImportMappingsDialog } from './ImportMappingsDialog';
import { MappingDialog } from './MappingDialog';
import { MappingKindGuide } from './MappingKindGuide';
import { kindLabelKeys } from './mappingKinds';
import { mappingsToJson } from './mappingsJson';

type ExportChoice = { format: 'json' | 'xlsx'; kind: MappingKind | undefined };

/**
 * The tenant's code mappings — how each of its own codes translates to the standard one. This is
 * what shapes the invoices, so it is a section of its own: rows are edited one at a time here, or
 * as a whole through export and import, in JSON or as an Excel workbook.
 */
export function MappingsPage() {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();
  // Nothing to query: an administrator has not picked a tenant yet, or the caller holds none.
  const hasNoTenant = selectedTenantGid === null;
  const noTenantKey = useNoTenantMessageKey();

  const [type, setType] = useState<MappingKind>('Unit');
  const [editing, setEditing] = useState<TenantMapping | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TenantMapping | null>(null);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  // A tenant-less query reaches the fallback schema and fails instead of returning an empty list.
  const query = useQuery({
    queryKey: queryKeys.mappings.list(selectedTenantGid, type),
    queryFn: ({ signal }) => listMappings(type, signal),
    enabled: selectedTenantGid !== null,
  });

  const toggleMutation = useMutation({
    mutationFn: (mapping: TenantMapping) =>
      upsertMapping({
        type: mapping.type,
        sourceValue: mapping.sourceValue,
        targetValue: mapping.targetValue,
        secondaryValue: mapping.secondaryValue,
        description: mapping.description,
        enabled: !mapping.enabled,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mappings.all });
    },
    onError: toastError,
  });

  const deleteMutation = useMutation({
    mutationFn: (mappingGid: string) => deleteMapping(mappingGid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mappings.all });
      toastSuccess(t('mappings.deleted'));
      setPendingDelete(null);
    },
    onError: (error) => {
      toastError(error);
      setPendingDelete(null);
    },
  });

  const exportMutation = useMutation({
    mutationFn: async ({ format, kind }: ExportChoice) => {
      const stem = `mappings-${(kind ?? 'all').toLowerCase()}`;
      if (format === 'xlsx') {
        await downloadApiFile(mappingsWorkbookExportPath, {
          query: { type: kind },
          fallbackFileName: `${stem}.xlsx`,
        });
        return;
      }
      const document = await exportMappings(kind);
      saveBlob(new Blob([mappingsToJson(document.mappings)], { type: 'application/json' }), `${stem}.json`);
    },
    onError: toastError,
  });

  const chooseExport = (choice: ExportChoice) => {
    setExportAnchor(null);
    exportMutation.mutate(choice);
  };

  const mappings = query.data ?? [];
  const kindLabel = t(kindLabelKeys[type]);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5">{t('mappings.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('mappings.description')}
          </Typography>
        </Box>
        <Tooltip title={hasNoTenant ? t(noTenantKey) : ''}>
          <Stack direction="row" spacing={1} component="span" sx={{ flexShrink: 0 }}>
            <Button
              startIcon={<UploadFileIcon />}
              variant="outlined"
              disabled={hasNoTenant}
              onClick={() => setImporting(true)}
            >
              {t('mappings.import.button')}
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              endIcon={<ArrowDropDownIcon />}
              variant="outlined"
              disabled={hasNoTenant || exportMutation.isPending}
              onClick={(event) => setExportAnchor(event.currentTarget)}
            >
              {t('mappings.export.button')}
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              disabled={hasNoTenant}
              onClick={() => setEditing('new')}
            >
              {t('mappings.addMapping')}
            </Button>
          </Stack>
        </Tooltip>
        <Menu anchorEl={exportAnchor} open={exportAnchor !== null} onClose={() => setExportAnchor(null)}>
          <MenuItem onClick={() => chooseExport({ format: 'xlsx', kind: undefined })}>
            {t('mappings.export.allXlsx')}
          </MenuItem>
          <MenuItem onClick={() => chooseExport({ format: 'json', kind: undefined })}>
            {t('mappings.export.allJson')}
          </MenuItem>
          <MenuItem onClick={() => chooseExport({ format: 'xlsx', kind: type })}>
            {t('mappings.export.kindXlsx', { kind: kindLabel })}
          </MenuItem>
          <MenuItem onClick={() => chooseExport({ format: 'json', kind: type })}>
            {t('mappings.export.kindJson', { kind: kindLabel })}
          </MenuItem>
        </Menu>
      </Box>

      <Alert severity="info">{t('mappings.deviationsNote')}</Alert>

      <Tabs
        value={type}
        onChange={(_event, value: MappingKind) => setType(value)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {mappingKinds.map((kind) => (
          <Tab key={kind} value={kind} label={t(kindLabelKeys[kind])} />
        ))}
      </Tabs>

      <MappingKindGuide kind={type} />

      {!hasNoTenant && query.isPending && <LoadingBlock />}
      {!hasNoTenant && query.isError && (
        <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />
      )}

      {hasNoTenant ? (
        <EmptyBlock message={t(noTenantKey)} />
      ) : (
        query.isSuccess &&
        (mappings.length === 0 ? (
          <EmptyBlock message={t('mappings.empty')} />
        ) : (
          <Paper variant="outlined">
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('mappings.columns.sourceValue')}</TableCell>
                    <TableCell>{t('mappings.columns.targetValue')}</TableCell>
                    <TableCell>{t('mappings.columns.secondary')}</TableCell>
                    <TableCell>{t('columns.description')}</TableCell>
                    <TableCell>{t('columns.enabled')}</TableCell>
                    <TableCell align="right">{t('columns.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((mapping) => (
                    <TableRow key={mapping.mappingGid} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{mapping.sourceValue}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{mapping.targetValue}</span>
                          {mapping.inActiveCodelist === false && (
                            <Tooltip title={t('mappings.notInCodelistHint')}>
                              <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                label={t('mappings.notInCodelist')}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{mapping.secondaryValue ?? '—'}</TableCell>
                      <TableCell>{mapping.description ?? '—'}</TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={mapping.enabled}
                          disabled={toggleMutation.isPending}
                          onChange={() => toggleMutation.mutate(mapping)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('actions.edit')}>
                          <IconButton size="small" onClick={() => setEditing(mapping)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('actions.delete')}>
                          <IconButton size="small" onClick={() => setPendingDelete(mapping)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}

      {editing !== null && (
        <MappingDialog
          type={type}
          mapping={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {importing && <ImportMappingsDialog onClose={() => setImporting(false)} />}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('mappings.confirmDeleteTitle')}
        message={t('mappings.confirmDeleteMessage', { source: pendingDelete?.sourceValue ?? '' })}
        confirmLabel={t('actions.delete')}
        confirmColor="error"
        busy={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.mappingGid)}
        onClose={() => setPendingDelete(null)}
      />
    </Stack>
  );
}
